/**
 * Authentication Store
 * 
 * Pinia store for managing authentication state.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import { authApi } from '@/services/apiService'
import { isLocalAuthBypassEnabled } from '@/config/authConfig'
import type { AuthUser } from '@/types'
import { Roles } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const buildLocalDevUser = (): AuthUser => ({
    id: 'local-dev-user',
    email: 'local.dev@localhost',
    displayName: 'Local Dev User',
    customerId: 'local-dev',
    roles: Object.values(Roles),
    registrationComplete: true,
  })

  // State
  const user = ref<AuthUser | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Computed
  const isAuthenticated = computed(() => user.value !== null)
  // User is considered "registered" if:
  // 1. Backend explicitly says registrationComplete: true, OR
  // 2. User has both a customerId AND non-empty roles (already registered in Graph)
  const isRegistrationComplete = computed(() => {
    if (!user.value) return false
    if (user.value.registrationComplete === true) return true
    // Fallback: if user has customerId AND roles, they're registered
    return user.value.customerId !== null && 
           user.value.customerId !== undefined &&
           user.value.roles.length > 0
  })
  const customerId = computed(() => user.value?.customerId ?? null)
  const roles = computed(() => user.value?.roles ?? [])

  // Role checks
  const isSuperAdmin = computed(() => roles.value.includes(Roles.SUPER_ADMIN))
  const isDeviceAdmin = computed(() => 
    roles.value.includes(Roles.SUPER_ADMIN) || roles.value.includes(Roles.DEVICE_ADMIN)
  )
  const isFileAdmin = computed(() =>
    roles.value.includes(Roles.SUPER_ADMIN) || roles.value.includes(Roles.FILE_ADMIN)
  )
  const isCustomerAdmin = computed(() =>
    roles.value.includes(Roles.SUPER_ADMIN) || roles.value.includes(Roles.CUSTOMER_ADMIN)
  )
  const isAnyAdmin = computed(() =>
    isSuperAdmin.value || isCustomerAdmin.value || isDeviceAdmin.value || isFileAdmin.value
  )
  const canManageDevices = computed(() => 
    isDeviceAdmin.value || roles.value.includes(Roles.DEVICE_USER)
  )
  const canManageFiles = computed(() =>
    isFileAdmin.value || roles.value.includes(Roles.FILE_USER)
  )
  const canManageUsers = computed(() =>
    isSuperAdmin.value || roles.value.includes(Roles.CUSTOMER_ADMIN)
  )
  const canAccessAnalysis = computed(() =>
    isSuperAdmin.value || roles.value.includes(Roles.SOR_ANALYST)
  )

  // Actions
  async function initialize(): Promise<void> {
    if (initialized.value) return

    try {
      isLoading.value = true
      error.value = null

      if (isLocalAuthBypassEnabled()) {
        user.value = buildLocalDevUser()
        initialized.value = true
        return
      }

      // Initialize MSAL
      await authService.initialize()

      // Check if user is authenticated
      if (authService.isAuthenticated()) {
        // Get user info from MSAL first
        const msalUser = await authService.getCurrentUser()
        
        if (msalUser) {
          // Then enrich with backend data
          try {
            const backendUser = await authApi.getMe()
            user.value = {
              ...msalUser,
              ...backendUser,
            }
          } catch (apiError) {
            console.warn('[Auth] Failed to get backend user info, using MSAL data:', apiError)
            user.value = msalUser
          }
        }
      }

      initialized.value = true
    } catch (err) {
      console.error('[Auth] Initialization error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to initialize authentication'
    } finally {
      isLoading.value = false
    }
  }

  async function login(): Promise<void> {
    try {
      error.value = null
      if (isLocalAuthBypassEnabled()) {
        user.value = buildLocalDevUser()
        initialized.value = true
        return
      }
      await authService.login()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      throw err
    }
  }

  async function logout(): Promise<void> {
    try {
      error.value = null
      if (isLocalAuthBypassEnabled()) {
        user.value = buildLocalDevUser()
        initialized.value = true
        return
      }
      await authService.logout()
      user.value = null
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Logout failed'
      throw err
    }
  }

  async function completeRegistration(displayName: string): Promise<void> {
    try {
      isLoading.value = true
      error.value = null

      const updatedUser = await authApi.completeRegistration({ displayName })

      if (user.value) {
        // Only mark as complete if the backend says so
        const status = updatedUser.registrationStatus
        const isComplete = status === 'complete' || (!status && updatedUser.registrationComplete)

        user.value = {
          ...user.value,
          ...updatedUser,
          registrationComplete: isComplete,
          registrationStatus: status ?? (isComplete ? 'complete' : null),
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Registration failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function refreshUser(): Promise<void> {
    if (isLocalAuthBypassEnabled()) {
      user.value = buildLocalDevUser()
      return
    }

    if (!authService.isAuthenticated()) return

    try {
      const backendUser = await authApi.getMe()
      if (user.value) {
        user.value = {
          ...user.value,
          ...backendUser,
        }
      }
    } catch (err) {
      console.error('[Auth] Failed to refresh user:', err)
    }
  }

  function hasRole(role: string): boolean {
    return roles.value.includes(role)
  }

  function hasAnyRole(requiredRoles: string[]): boolean {
    return requiredRoles.some(role => roles.value.includes(role))
  }

  function clearError(): void {
    error.value = null
  }

  return {
    // State
    user,
    isLoading,
    error,
    initialized,

    // Computed
    isAuthenticated,
    isRegistrationComplete,
    customerId,
    roles,
    isSuperAdmin,
    isDeviceAdmin,
    isFileAdmin,
    isCustomerAdmin,
    isAnyAdmin,
    canManageDevices,
    canManageFiles,
    canManageUsers,
    canAccessAnalysis,

    // Actions
    initialize,
    login,
    logout,
    completeRegistration,
    refreshUser,
    hasRole,
    hasAnyRole,
    clearError,
  }
})
